<template>
  <ModalComponent
    ref="modalRef"
    :submit-button-text="$t('common.close')"
    :dismissible="selectedEvent === null"
    size="xl"
    @close="handleModalClose"
  >
    <template #title>
      {{ $t("company_orders.order_details") }}
    </template>

    <template #body>
      <!-- Loading State -->
      <LoadingIndicatorComponent v-if="isLoading" />

      <div v-else>
        <div class="card border-0 shadow-sm mb-3">
          <div class="card-body p-3 p-lg-4">
            <div
              class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-start gap-3 mb-3"
            >
              <div>
                <h6
                  class="text-uppercase text-muted mb-2 fw-semibold section-label"
                >
                  {{ $t("company_orders.order_information") }}
                </h6>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span class="order-type-pill">
                    {{ formatOrderType(currentOrder.order_type) }}
                  </span>
                  <CompanyOrderStatusBadge
                    :status="displayStatus(currentOrder)"
                  />
                </div>
              </div>

              <div v-if="canConfirmOrder || canEditOrder" class="d-flex gap-2">
                <button
                  v-if="canEditOrder"
                  type="button"
                  class="btn btn-outline-primary"
                  @click="openEditModal"
                  :disabled="isConfirming"
                >
                  <FontAwesomeIcon icon="pen" class="me-1" />
                  {{ $t("company_orders.edit_order") }}
                </button>
                <button
                  v-if="canConfirmOrder"
                  type="button"
                  class="btn btn-success"
                  @click="showConfirmPrompt = true"
                  :disabled="isConfirming"
                >
                  <FontAwesomeIcon
                    :icon="isConfirming ? 'spinner' : 'check'"
                    :class="{ 'fa-spin': isConfirming }"
                    class="me-1"
                  />
                  {{ $t("company_orders.confirm_order") }}
                </button>
              </div>
            </div>

            <div class="row g-2 g-lg-3">
              <div class="col-md-6 col-xl-3">
                <div
                  class="overview-metric-card overview-metric-card-blue h-100"
                >
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.order_starter") }}
                  </div>
                  <div class="fw-semibold">
                    {{ currentOrder.order_starter_name ?? "-" }}
                  </div>
                </div>
              </div>

              <div class="col-md-6 col-xl-3">
                <div
                  class="overview-metric-card overview-metric-card-green h-100"
                >
                  <div class="text-muted small mb-1">
                    {{ $t("common.company") }}
                  </div>
                  <div class="fw-semibold">
                    {{ currentOrder.company_name ?? "-" }}
                  </div>
                </div>
              </div>

              <div class="col-md-6 col-xl-3">
                <div
                  class="overview-metric-card overview-metric-card-amber h-100"
                >
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.created") }}
                  </div>
                  <div class="fw-semibold">
                    {{ formatDateTime(currentOrder.created_at) }}
                  </div>
                </div>
              </div>

              <div class="col-md-6 col-xl-3">
                <div
                  class="overview-metric-card overview-metric-card-violet h-100"
                >
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.updated") }}
                  </div>
                  <div class="fw-semibold">
                    {{ formatDateTime(currentOrder.updated_at) }}
                  </div>
                </div>
              </div>

              <div
                v-if="currentOrder.source_portfolio_name"
                class="col-md-6 col-xl-3"
              >
                <div class="overview-metric-card h-100">
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.portfolio") }}
                  </div>
                  <div class="fw-semibold">
                    {{ currentOrder.source_portfolio_name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Type Specific Details -->
        <div class="card border-0 shadow-sm mb-3">
          <div class="card-body p-3 p-lg-4">
            <h6
              class="text-uppercase text-muted mb-3 fw-semibold section-label"
            >
              {{
                $t("company_orders.order_type_details", {
                  type: formatOrderType(currentOrder.order_type),
                })
              }}
            </h6>
            <CnmcOrderFilings
              v-if="currentOrder.execution_method === 'CNMC_FORMS'"
              :isAdmin="is_admin"
              :company-id="currentOrder.company_id"
              :order-id="currentOrder.id"
              @update="refreshAfterFilingAction"
            />
            <component
              v-else-if="orderDetailsConfig"
              :is="orderDetailsConfig.component"
              v-bind="orderDetailsConfig.props"
            />
          </div>
        </div>

        <OrderRegistryTransactionsCard
          v-if="orderDetails?.connected_events"
          :events="orderDetails.connected_events"
          @view:event="selectedEvent = $event"
        />

        <EditCompanyOrderComponent
          v-if="showEditModal"
          :order="order"
          @close="handleEditClose"
          @order:updated="handleOrderUpdated"
        />

        <!-- Portfolio Items Table -->
        <div v-if="showPortfolioItems" class="card border-0 shadow-sm">
          <div class="card-body p-3 p-lg-4">
            <h6
              class="text-uppercase text-muted mb-3 fw-semibold section-label"
            >
              {{ $t("company_orders.associated_portfolio_items") }}
            </h6>
            <PortfolioItemsTable
              :items="orderItems"
              :disable-selection="true"
              :enable-count-editing="false"
            />
          </div>
        </div>
      </div>
    </template>
  </ModalComponent>

  <RegistryEventModal
    v-if="selectedEvent"
    :event="selectedEvent"
    @close="selectedEvent = null"
  />

  <!-- Confirm Order Confirmation Prompt -->
  <ConfirmationModalComponent
    :visible="showConfirmPrompt"
    :title="$t('company_orders.confirm_order')"
    :message="$t('company_orders.confirm_order_message')"
    confirm-button-class="btn-success"
    @confirm="confirmOrder"
    @cancel="showConfirmPrompt = false"
  />
</template>

<script setup lang="ts">
import CnmcOrderFilings from "@/dashboard/production_strategy/issuance-reporting/es/CnmcOrderFilings.vue";
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatDateTime } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import type { Component } from "vue";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import CancelOrderDetailsSection from "../cancel/CancelOrderDetailsSection.vue";
import { companyOrderApi } from "../common/companyOrderApi";
import type {
  BuyAndCancelOrderDetails,
  CancelOrderDetails,
  CompanyOrder,
  CompanyOrderDetailsResponse,
  SellOrderDetails,
  SendOrderDetails,
} from "../common/companyOrderTypes";
import { OrderType } from "../common/companyOrderTypes";
import { useCompanyOrderStore } from "../common/useCompanyOrderStore";
import CompanyOrderStatusBadge from "./CompanyOrderStatusBadge.vue";
import EditCompanyOrderComponent from "./EditCompanyOrderComponent.vue";
import { displayStatus } from "./orderDisplayStatus";
import OrderRegistryTransactionsCard from "./OrderRegistryTransactionsCard.vue";
import RegistryEventModal from "./RegistryEventModal.vue";
import type { RegistryEventSummary } from "./transactionLogTypes";
import BuyAndCancelOrderDetailsSection from "./details/BuyAndCancelOrderDetailsSection.vue";
import BuyOrderDetailsSection from "./details/BuyOrderDetailsSection.vue";
import GenericOrderDetailsSection from "./details/GenericOrderDetailsSection.vue";
import SellOrderDetailsSection from "./details/SellOrderDetailsSection.vue";
import SendOrderDetailsSection from "./details/SendOrderDetailsSection.vue";

interface Props {
  order: CompanyOrder;
  is_admin?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

const portfolioItemsStore = usePortfolioItemsStore();
const companyOrderStore = useCompanyOrderStore();
const { globalErrorHandler } = useGlobalHandlers();
const { t } = useI18n();

// State
const orderDetails = ref<CompanyOrderDetailsResponse | null>(null);
const isLoading = ref(false);
const isConfirming = ref(false);
const showEditModal = ref(false);
const selectedEvent = ref<RegistryEventSummary | null>(null);
const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");

// Header data (status, updated_at, total_selected_mwh, ...) must reflect the
// latest server state after an in-place edit, so it tracks the GET response
// rather than the stale prop.
const currentOrder = computed<CompanyOrder>(
  () => orderDetails.value ?? props.order
);

const canEditOrder = computed(() =>
  currentOrder.value.allowed_actions.includes("EDIT")
);

const canConfirmOrder = computed(() =>
  currentOrder.value.allowed_actions.includes("CONFIRM")
);

// Derived from the store so a refetch (e.g. by the edit modal) never shows another order's items.
const orderItems = computed(() =>
  portfolioItemsStore.allItems.filter(
    (item) => item.company_order_id === props.order.id
  )
);

const showPortfolioItems = computed(() => orderItems.value.length > 0);

// Handle modal close
const handleModalClose = () => {
  emit("close");
};

// Order details component configuration
const orderDetailsConfig = computed(() => {
  if (!orderDetails.value) return null;

  const order = currentOrder.value;
  const componentMap: Record<OrderType, { component: Component; props: any }> =
    {
      [OrderType.SEND]: {
        component: SendOrderDetailsSection,
        props: {
          details: orderDetails.value.order_details as SendOrderDetails,
          totalSelectedMwh: order.total_selected_mwh,
        },
      },
      [OrderType.CANCEL]: {
        component: CancelOrderDetailsSection,
        props: {
          details: orderDetails.value.order_details as CancelOrderDetails,
          totalSelectedMwh: order.total_selected_mwh,
        },
      },
      [OrderType.SELL]: {
        component: SellOrderDetailsSection,
        props: {
          details: orderDetails.value.order_details as SellOrderDetails,
          totalSelectedMwh: order.total_selected_mwh,
        },
      },
      [OrderType.BUY]: {
        component: BuyOrderDetailsSection,
        props: {
          details: {
            ...orderDetails.value.order_details,
            company_id: order.company_id,
          },
          isAdmin: props.is_admin,
          orderCreatedAt: order.created_at,
          orderStatus: order.status,
        },
      },
      [OrderType.RECEIVE]: {
        component: GenericOrderDetailsSection,
        props: {
          details: orderDetails.value.order_details,
        },
      },
      [OrderType.BUY_AND_CANCEL]: {
        component: BuyAndCancelOrderDetailsSection,
        props: {
          details: orderDetails.value.order_details as BuyAndCancelOrderDetails,
          orderCreatedAt: order.created_at,
          orderStatus: order.status,
        },
      },
    };

  // Return the specific component config or fallback to generic
  return (
    componentMap[order.order_type] || {
      component: GenericOrderDetailsSection,
      props: {
        details: orderDetails.value.order_details,
      },
    }
  );
});

// Methods
/**
 * Fetch order details from the API
 */
const fetchOrderDetails = async () => {
  try {
    isLoading.value = true;
    const response = await companyOrderApi.getOrderDetails(props.order.id);
    orderDetails.value = response.data;
  } finally {
    isLoading.value = false;
  }
};

/**
 * A CNMC filing action may move the order (e.g. a discarded filing cancels it). Refetch without
 * the loading state, which would remount the filing card, and replace the order's list row with the
 * server's. The portfolio is refetched alongside, as a discarded filing releases its reserved GOs;
 * the order's status does not wait on it.
 */
const refreshAfterFilingAction = async () => {
  const portfolioRefetch = portfolioItemsStore
    .refetchItems()
    .catch(globalErrorHandler);
  try {
    const response = await companyOrderApi.getOrderDetails(props.order.id);
    orderDetails.value = response.data;
    companyOrderStore.replaceOrder(response.data);
  } catch (error) {
    globalErrorHandler(error);
  }
  await portfolioRefetch;
};

const openEditModal = () => {
  showEditModal.value = true;
};

const handleEditClose = () => {
  showEditModal.value = false;
};

const handleOrderUpdated = async (_orderId: number) => {
  showEditModal.value = false;
  await Promise.allSettled([
    fetchOrderDetails(),
    portfolioItemsStore.refetchItems(),
  ]);
};

const showConfirmPrompt = ref(false);

const confirmOrder = async () => {
  showConfirmPrompt.value = false;
  try {
    isConfirming.value = true;
    await companyOrderStore.confirmOrder(props.order.id);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isConfirming.value = false;
  }
};

/**
 * Format order type for display
 */
const formatOrderType = (type: OrderType): string => {
  const key = `company_orders.order_types.${type}`;
  const translated = t(key);
  return translated !== key ? translated : type;
};

// Lifecycle
onMounted(() => {
  fetchOrderDetails();

  // Show the modal
  if (modalRef.value) {
    modalRef.value.showModal = true;
  }
});
</script>

<style scoped lang="scss">
@use "./order-details-shared";

.order-type-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--phoenix-primary-border-subtle);
  border-radius: 999px;
  background: var(--phoenix-primary-bg-subtle);
  color: var(--phoenix-primary-text-emphasis);
  font-size: 0.8rem;
  font-weight: 600;
}

.overview-metric-card {
  height: 100%;
  padding: 1rem;
  border: 1px solid var(--phoenix-secondary-border-subtle);
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(var(--phoenix-emphasis-color-rgb), 0.03);
}

.overview-metric-card-blue {
  background: var(--phoenix-primary-bg-subtle);
  border-color: var(--phoenix-primary-border-subtle);
}

.overview-metric-card-green {
  background: var(--phoenix-success-bg-subtle);
  border-color: var(--phoenix-success-border-subtle);
}

.overview-metric-card-amber {
  background: var(--phoenix-warning-bg-subtle);
  border-color: var(--phoenix-warning-border-subtle);
}

.overview-metric-card-violet {
  background: var(--phoenix-info-bg-subtle);
  border-color: var(--phoenix-info-border-subtle);
}
</style>
